import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  forwardRef,
  input,
  linkedSignal,
  PLATFORM_ID,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  RedeLayout,
  RedeProfundidade,
  VisaoArvoreResponse,
  VisaoGrafoResponse,
} from '@interfaces/corporate-registry/responses/rede-societaria.response';
import {
  VisaoControleResponse,
  VisaoEstruturaResponse,
  VisaoGeoResponse,
  VisaoMatrizResponse,
  VisaoRelacoesResponse,
} from '@interfaces/corporate-registry/responses/rede-visoes.response';
import { CorporateRegistryCacheService } from '@services/http/corporate-registry/corporate-registry-cache.service';
import { CorporateRegistryHttpService } from '@services/http/corporate-registry/corporate-registry-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { catchError, map, Observable, of, retry, Subject, switchMap } from 'rxjs';
import { RegistryControlComponent } from '@shared/components/ownership-network/registry-control/registry-control.component';
import { layoutDeForca } from '@shared/components/ownership-network/registry-force-layout';
import { RegistryGeoMapComponent } from '@shared/components/ownership-network/registry-geo-map/registry-geo-map.component';
import { CORES_GRAFO } from '@shared/components/ownership-network/registry-graph-geometry';
import { RegistryGraphComponent } from '@shared/components/ownership-network/registry-graph/registry-graph.component';
import { RegistryMatrixComponent } from '@shared/components/ownership-network/registry-matrix/registry-matrix.component';
import { RegistryRelationsComponent } from '@shared/components/ownership-network/registry-relations/registry-relations.component';
import { RegistryStructuresComponent } from '@shared/components/ownership-network/registry-structures/registry-structures.component';
import { RegistryTreeNodeComponent } from '@shared/components/ownership-network/registry-tree-node/registry-tree-node.component';
import {
  OWNERSHIP_NETWORK_DISPLAY,
  OwnershipNetworkDisplay,
} from '@shared/components/ownership-network/ownership-network-display';

export type VisaoRede =
  | 'mapa'
  | 'geo'
  | 'arvore'
  | 'matriz'
  | 'relacoes'
  | 'estrutura'
  | 'controle';
/** `conexoes` = sem layout no servidor; posição calculada no cliente (`layoutDeForca`). */
export type LayoutUi = 'conexoes' | RedeLayout;

type Estado =
  | { tipo: 'carregando' }
  | { tipo: 'erro' }
  | { tipo: 'mapa'; dado: VisaoGrafoResponse }
  | { tipo: 'geo'; dado: VisaoGeoResponse }
  | { tipo: 'arvore'; dado: VisaoArvoreResponse }
  | { tipo: 'matriz'; dado: VisaoMatrizResponse }
  | { tipo: 'relacoes'; dado: VisaoRelacoesResponse }
  | { tipo: 'estrutura'; dado: VisaoEstruturaResponse }
  | { tipo: 'controle'; dado: VisaoControleResponse };

interface Consulta {
  visao: VisaoRede;
  prof: RedeProfundidade;
  layout: LayoutUi;
  tentativa: number;
}

/** Todas as visões, na ordem do Dossiê. `availableViews` filtra, mas não reordena. */
const TODAS_AS_VISOES: { valor: VisaoRede; rotulo: string }[] = [
  { valor: 'mapa', rotulo: 'Mapa' },
  { valor: 'geo', rotulo: 'Geo' },
  { valor: 'arvore', rotulo: 'Árvore' },
  { valor: 'matriz', rotulo: 'Matriz' },
  { valor: 'relacoes', rotulo: 'Relações' },
  { valor: 'estrutura', rotulo: 'Estrutura' },
  { valor: 'controle', rotulo: 'Controle' },
];

/** Os textos do `CARREGANDO_POR_VISAO` do Dossiê. */
const MENSAGEM_CARREGANDO: Record<VisaoRede, string> = {
  mapa: 'Carregando o mapa societário…',
  geo: 'Localizando as empresas no mapa…',
  arvore: 'Montando a árvore societária…',
  matriz: 'Montando a matriz sócio × empresa…',
  relacoes: 'Levantando os vínculos…',
  estrutura: 'Analisando as estruturas de participação…',
  controle: 'Projetando o controle societário…',
};

/**
 * "Mapa societário" — réplica da `RedeSection` do Vault-Dossiê.
 *
 * Como lá, há duas consultas:
 * - a **base** (`rede/grafo` sem layout, na profundidade escolhida) diz se existe rede para
 *   mostrar e qual profundidade o "Auto" usou (`→ N`). Enquanto ela carrega, nem as visões nem o
 *   Expandir aparecem; com 1 nó ou menos, a seção diz "Sem vínculos societários para plotar.";
 * - a **ativa** é a da visão escolhida. Em Mapa + Conexões é a MESMA chave de cache da base,
 *   então não há segunda requisição.
 *
 * ⚠️ A visão Geo é **paga** (geocoding): só é consultada com a aba aberta, e fica no cache por
 * tela — voltar a ela não gera outra chamada.
 */
@Component({
  selector: 'app-registry-network-card',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    RegistryGraphComponent,
    RegistryTreeNodeComponent,
    RegistryMatrixComponent,
    RegistryRelationsComponent,
    RegistryStructuresComponent,
    RegistryControlComponent,
    RegistryGeoMapComponent,
  ],
  templateUrl: './registry-network-card.component.html',
  styleUrl: './registry-network-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    // As visões internas leem o destaque da raiz e o selo de holding daqui (sem input por input).
    {
      provide: OWNERSHIP_NETWORK_DISPLAY,
      useExisting: forwardRef(() => RegistryNetworkCardComponent),
    },
  ],
})
export class RegistryNetworkCardComponent implements OwnershipNetworkDisplay {
  readonly cnpj = input.required<string>();

  // Configuração opcional. Os padrões reproduzem exatamente o comportamento de Deals.
  /** Título do card e da tela cheia. */
  readonly title = input<string>('Mapa societário');
  /** Visão aberta ao carregar. Fora de `availableViews`, vale a primeira disponível. */
  readonly initialView = input<VisaoRede>('mapa');
  /** Profundidade ao carregar. */
  readonly initialDepth = input<RedeProfundidade>('auto');
  /** Visões oferecidas, na ordem da barra. `null` (padrão) = todas. */
  readonly availableViews = input<readonly VisaoRede[] | null>(null);
  /** Acordeão: `true` (padrão) nasce aberto; `false` nasce recolhido, só com o título. */
  readonly startExpanded = input<boolean>(true);
  /** Empresa consultada com cor e raio próprios (verde, maior) no grafo, na Geo e na Matriz. */
  readonly highlightRoot = input<boolean>(true);
  /** Selo "Possível holding" nas visões Controle e Estrutura. */
  readonly showHoldingBadge = input<boolean>(true);

  /** Acordeão: o título recolhe e expande (pedido de 25/09/2026, card 2986). */
  protected readonly aberto = linkedSignal(() => this.startExpanded());

  private readonly http = inject(CorporateRegistryHttpService);
  private readonly cache = inject(CorporateRegistryCacheService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly cores = CORES_GRAFO;
  protected readonly profundidades: RedeProfundidade[] = ['auto', 1, 2, 3, 4, 5, 6];
  protected readonly visoes = computed(() => {
    const filtro = this.availableViews();
    if (!filtro?.length) return TODAS_AS_VISOES;
    const escolhidas = TODAS_AS_VISOES.filter(v => filtro.includes(v.valor));
    return escolhidas.length ? escolhidas : TODAS_AS_VISOES;
  });
  protected readonly layouts: { valor: LayoutUi; rotulo: string }[] = [
    { valor: 'conexoes', rotulo: 'Conexões' },
    { valor: 'hierarquico', rotulo: 'Camadas' },
    { valor: 'radial', rotulo: 'Radial' },
    { valor: 'horizontal', rotulo: 'Fluxo' },
  ];
  protected readonly mensagemCarregando = MENSAGEM_CARREGANDO;

  protected readonly visao = linkedSignal<VisaoRede>(() => {
    const inicial = this.initialView();
    const visoes = this.visoes();
    return visoes.some(v => v.valor === inicial) ? inicial : visoes[0].valor;
  });
  protected readonly prof = linkedSignal<RedeProfundidade>(() => this.initialDepth());
  protected readonly layout = signal<LayoutUi>('conexoes');
  protected readonly telaCheia = signal(false);
  private readonly tentativa = signal(0);

  /**
   * Recorte base: existe rede? qual profundidade o "auto" usou? `erro` separa "a consulta falhou"
   * de "não há vínculos" — sem isso, o Dossiê fora do ar apareceria como um fato sobre a empresa.
   */
  protected readonly base = signal<{
    carregando: boolean;
    grafo: VisaoGrafoResponse | null;
    erro: boolean;
  }>({ carregando: true, grafo: null, erro: false });
  /** A visão ativa. */
  protected readonly estado = signal<Estado>({ tipo: 'carregando' });

  protected readonly semDados = computed(() => {
    const b = this.base();
    return !b.carregando && !b.erro && (!b.grafo || b.grafo.nos.length <= 1);
  });

  private readonly botaoExpandir = viewChild<ElementRef<HTMLButtonElement>>('expandir');
  private readonly botaoFechar = viewChild<ElementRef<HTMLButtonElement>>('fecharCheia');
  private readonly documento = inject(DOCUMENT);
  private readonly noNavegador = isPlatformBrowser(inject(PLATFORM_ID));

  protected readonly pronto = computed(
    () => !this.base().carregando && !this.base().erro && !this.semDados()
  );

  protected readonly profundidadeUsada = computed(
    () => this.base().grafo?.meta?.profundidadeUsada ?? null
  );

  /** Geo e Matriz têm legenda própria, como no Dossiê. */
  protected readonly mostraLegenda = computed(() => {
    const v = this.visao();
    return this.pronto() && v !== 'geo' && v !== 'matriz';
  });

  protected readonly avisos = computed(() => {
    const e = this.estado();
    return 'dado' in e ? (e.dado.meta?.avisos ?? []) : [];
  });

  protected readonly truncado = computed(() => {
    const e = this.estado();
    return e.tipo === 'mapa' && e.dado.truncado ? e.dado.nos.length : null;
  });

  protected readonly rotuloVisao = computed(
    () => this.visoes().find(v => v.valor === this.visao())?.rotulo ?? ''
  );

  private readonly consultasBase = new Subject<{ prof: RedeProfundidade; tentativa: number }>();
  private readonly consultas = new Subject<Consulta>();

  constructor() {
    this.consultasBase
      .pipe(
        switchMap(c => {
          this.base.set({ carregando: true, grafo: null, erro: false });
          return this.grafo(c.prof, 'conexoes').pipe(
            map(grafo => ({ carregando: false, grafo, erro: false })),
            catchError(() => of({ carregando: false, grafo: null, erro: true }))
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(b => this.base.set(b));

    this.consultas
      .pipe(
        switchMap(c => {
          this.estado.set({ tipo: 'carregando' });
          return this.buscar(c).pipe(catchError(() => of<Estado>({ tipo: 'erro' })));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(e => this.estado.set(e));

    effect(() => {
      const c = { prof: this.prof(), tentativa: this.tentativa() };
      untracked(() => this.consultasBase.next(c));
    });

    // A visão ativa só é consultada com a base pronta — como no Dossiê, as abas nem existem antes.
    // É o que impede a Geo (PAGA) de ser chamada enquanto a tela mostra "Carregando o mapa…", ou
    // uma vez por profundidade quando a pessoa clica 1→2→…→6 com a Geo aberta.
    effect(() => {
      if (!this.pronto()) return;
      const c: Consulta = {
        visao: this.visao(),
        prof: this.prof(),
        layout: this.layout(),
        tentativa: this.tentativa(),
      };
      untracked(() => this.consultas.next(c));
    });

    // Tela cheia é modal: trava a rolagem da página, leva o foco ao ✕ e devolve ao "Expandir".
    effect(() => {
      const aberta = this.telaCheia();
      if (!this.noNavegador) return;
      untracked(() => {
        this.documento.body.style.overflow = aberta ? 'hidden' : '';
        setTimeout(() =>
          (aberta ? this.botaoFechar() : this.botaoExpandir())?.nativeElement.focus()
        );
      });
    });
    this.destroyRef.onDestroy(() => {
      if (this.noNavegador) this.documento.body.style.overflow = '';
    });
  }

  protected tentarDeNovo(): void {
    this.tentativa.update(t => t + 1);
  }

  /**
   * Esc fecha a tela cheia — mas, com um popover do grafo aberto, o Esc é dele (fecha só o popover).
   * Os dois ouvem o `document`, e o do card é registrado primeiro; por isso a checagem aqui.
   */
  @HostListener('document:keydown.escape')
  protected sairDaTelaCheia(): void {
    if (!this.telaCheia()) return;
    if (this.documento.querySelector('.cheia app-registry-graph .pop')) return;
    this.telaCheia.set(false);
  }

  /** Grafo da profundidade/layout; Conexões é posicionado AQUI, dentro da fonte do cache. */
  private grafo(prof: RedeProfundidade, layout: LayoutUi): Observable<VisaoGrafoResponse> {
    const cnpj = this.cnpj();
    const servidor = layout === 'conexoes' ? null : layout;
    return this.cache.obter(`${cnpj}|mapa|${prof}|${layout}`, () =>
      semNulo(this.http.getRedeGrafo(cnpj, prof, servidor)).pipe(
        map(g => (servidor ? g : { ...g, nos: layoutDeForca(g.nos, g.arestas) }))
      )
    );
  }

  private buscar(c: Consulta): Observable<Estado> {
    const cnpj = this.cnpj();
    const chave = `${cnpj}|${c.visao}|${c.prof}`;
    const obter = <T>(fonte: () => Observable<T | null>): Observable<T> =>
      this.cache.obter(chave, () => semNulo(fonte()));
    const embrulhar = <T>(tipo: Estado['tipo'], fonte$: Observable<T>): Observable<Estado> =>
      fonte$.pipe(map(dado => ({ tipo, dado }) as Estado));

    switch (c.visao) {
      case 'mapa':
        return embrulhar('mapa', this.grafo(c.prof, c.layout));
      case 'geo':
        // ⚠️ A 1ª consulta de um recorte geocodifica os endereços em lote no Vault-Dossiê e, às
        // vezes, falha (medido em 25/09/2026: EMBRAER em Auto deu 422 em 23s e 200 logo em
        // seguida). O Dossiê guarda as coordenadas que já resolveu, então UMA nova tentativa sai
        // quase toda do cache — custo perto de zero — e evita a pessoa ver erro à toa. Fica
        // dentro da fonte do cache: só o resultado final é guardado.
        return embrulhar(
          'geo',
          obter(() =>
            this.http
              .getRedeGeo(cnpj, c.prof)
              .pipe(retry<VisaoGeoResponse | null>({ count: 1, delay: 2000 }))
          )
        );
      case 'arvore':
        return embrulhar(
          'arvore',
          obter(() => this.http.getRedeArvore(cnpj, c.prof))
        );
      case 'matriz':
        return embrulhar(
          'matriz',
          obter(() => this.http.getRedeMatriz(cnpj, c.prof))
        );
      case 'relacoes':
        return embrulhar(
          'relacoes',
          obter(() => this.http.getRedeRelacoes(cnpj, c.prof))
        );
      case 'estrutura':
        return embrulhar(
          'estrutura',
          obter(() => this.http.getRedeEstrutura(cnpj, c.prof))
        );
      case 'controle':
        return embrulhar(
          'controle',
          obter(() => this.http.getRedeControle(cnpj, c.prof))
        );
    }
  }
}

/**
 * Resposta vazia vira ERRO dentro da fonte do cache: assim ela não fica guardada, e "Tentar de
 * novo" refaz a chamada (um `null` em cache seria um erro para sempre).
 */
function semNulo<T>(fonte$: Observable<T | null>): Observable<T> {
  return fonte$.pipe(
    map(d => {
      if (d === null || d === undefined) throw new Error('Resposta vazia do registro empresarial.');
      return d;
    })
  );
}
